import { gql } from '@apollo/client';

// Category Mutations
export const CREATE_CATEGORY = gql`
  mutation CreateCategory($category: CategoryInput!) {
    createCategory(category: $category) {
      id
      code
      name
      description
      status
      position
      imageData
      imageFileName
      imageContentType
      imageGenerated
      imageGeneratedAt
      createdDate
      lastModifiedDate
    }
  }
`;

export const UPDATE_CATEGORY = gql`
  mutation UpdateCategory($id: ID!, $category: CategoryInput!) {
    updateCategory(id: $id, category: $category) {
      id
      code
      name
      description
      status
      position
      imageData
      imageFileName
      imageContentType
      imageGenerated
      imageGeneratedAt
      createdDate
      lastModifiedDate
    }
  }
`;

export const DELETE_CATEGORY = gql`
  mutation DeleteCategory($code: String!) {
    deleteCategory(code: $code)
  }
`;

// Item Mutations
export const CREATE_ITEM = gql`
  mutation CreateItem($input: ItemInput!) {
    createItem(input: $input) {
      id
      code
      name
      description
      categoryCode
      subCategoryCode
      status
      position
      associatedCategoryCodes
      imageData
      imageFileName
      imageGenerated
      createdDate
      updatedDate
      soundOptions {
        voiceId
        soundData
        soundFileName
        soundContentType
        soundGenerated
        soundGeneratedAt
      }
    }
  }
`;

export const UPDATE_ITEM = gql`
  mutation UpdateItem($id: ID!, $item: ItemInput!) {
    updateItem(id: $id, item: $item) {
      id
      code
      name
      description
      categoryCode
      subCategoryCode
      status
      position
      associatedCategoryCodes
      imageData
      imageFileName
      imageGenerated
      createdDate
      updatedDate
      soundOptions {
        voiceId
        soundData
        soundFileName
        soundContentType
        soundGenerated
        soundGeneratedAt
      }
      __typename
    }
  }
`;

export const DELETE_ITEM = gql`
  mutation DeleteItem($id: ID!) {
    deleteItem(id: $id)
  }
`;

// Audio Mutations
export const GENERATE_AUDIO = gql`
  mutation GenerateAudio($input: AudioInput!) {
    generateAudio(input: $input) {
      audioBase64
      text
      voiceId
      generatedAt
    }
  }
`;

export const GENERATE_AUDIO_ALL_VOICES = gql`
  mutation GenerateAudioAllVoices($text: String!) {
    generateAudioAllVoices(text: $text) {
      voiceId
      voiceName
      audioBase64
    }
  }
`;

export const GENERATE_CATEGORY_AUDIO = gql`
  mutation GenerateCategoryAudio($input: AudioInput!) {
    generateAudio(input: $input) {
      audioBase64
      text
      voiceId
      generatedAt
    }
  }
`;

export const UPLOAD_AUDIO = gql`
  mutation UploadAudio($itemCode: String!, $audioFile: Upload!) {
    uploadAudio(itemCode: $itemCode, audioFile: $audioFile) {
      success
      message
      audioUrl
    }
  }
`;

// Image Mutations
export const GENERATE_IMAGE = gql`
  mutation GenerateImage($input: ImageInput!) {
    generateImage(input: $input) {
      imageUrl
      imageBase64
      prompt
      size
      quality
      generatedAt
    }
  }
`;

export const GENERATE_CATEGORY_IMAGE = gql`
  mutation GenerateCategoryImage($input: ImageInput!) {
    generateImage(input: $input) {
      imageUrl
      imageBase64
      prompt
      size
      quality
      generatedAt
    }
  }
`;

export const UPLOAD_IMAGE = gql`
  mutation UploadImage($itemCode: String!, $imageFile: Upload!) {
    uploadImage(itemCode: $itemCode, imageFile: $imageFile) {
      success
      message
      imageUrl
    }
  }
`;

// Voice Mutations
export const CREATE_VOICE = gql`
  mutation CreateVoice($voice: VoiceInput!) {
    createVoice(voice: $voice) {
      id
      voiceId
      name
      description
      isActive
      isDefault
      createdDate
    }
  }
`;

export const UPDATE_VOICE = gql`
  mutation UpdateVoice($id: String!, $voice: VoiceInput!) {
    updateVoice(id: $id, voice: $voice) {
      id
      voiceId
      name
      description
      isActive
      isDefault
      createdDate
    }
  }
`;

export const DELETE_VOICE = gql`
  mutation DeleteVoice($id: String!) {
    deleteVoice(id: $id)
  }
`;

// User/Security Mutations

// Source Mutations
export const CREATE_SOURCE = gql`
  mutation CreateSource($input: SourceInput!) {
    createSource(input: $input) {
      id
      code
      name
      description
      status
      tenantId
      createdDate
      lastModifiedDate
    }
  }
`;

export const UPDATE_SOURCE = gql`
  mutation UpdateSource($id: String!, $input: SourceInput!) {
    updateSource(id: $id, input: $input) {
      id
      code
      name
      description
      status
      tenantId
      createdDate
      lastModifiedDate
    }
  }
`;

export const DELETE_SOURCE = gql`
  mutation DeleteSource($id: String!) {
    deleteSource(id: $id)
  }
`;

// Role Mutations
export const CREATE_ROLE = gql`
  mutation CreateRole($input: RoleInput!) {
    createRole(input: $input) {
      id
      roleId
      name
      description
      isActive
      createdDate
    }
  }
`;

export const UPDATE_ROLE = gql`
  mutation UpdateRole($id: String!, $input: RoleInput!) {
    updateRole(id: $id, input: $input) {
      id
      roleId
      name
      description
      isActive
      createdDate
    }
  }
`;

export const DELETE_ROLE = gql`
  mutation DeleteRole($id: String!) {
    deleteRole(id: $id)
  }
`;

// Tenant Mutations
export const CREATE_TENANT = gql`
  mutation CreateTenant($input: TenantInput!) {
    createTenant(input: $input) {
      id
      name
      description
      isActive
      createdDate
    }
  }
`;

export const UPDATE_TENANT = gql`
  mutation UpdateTenant($id: String!, $input: TenantInput!) {
    updateTenant(id: $id, input: $input) {
      id
      name
      description
      isActive
      createdDate
    }
  }
`;

export const DELETE_TENANT = gql`
  mutation DeleteTenant($id: String!) {
    deleteTenant(id: $id)
  }
`;

// User Mutations
export const CREATE_USER = gql`
  mutation CreateUser($input: UserInput!) {
    createUser(input: $input) {
      id
      username
      emailAddress
      firstName
      lastName
      displayName
      languageCode
      identityProvider
      linkId
      roleId
      mappings {
        tenantId
        roleId
      }
      createdDate
      isActive
      lastLoginDate
      notificationPreferences {
        enabled
        smsEnabled
        phoneNumber
        signalREnabled
        updatedDate
      }
      notificationGroups {
        userId
        status
      }
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($id: String!, $input: UserInput!) {
    updateUser(id: $id, input: $input) {
      id
      username
      emailAddress
      firstName
      lastName
      displayName
      languageCode
      identityProvider
      linkId
      roleId
      mappings {
        tenantId
        roleId
      }
      createdDate
      isActive
      lastLoginDate
      notificationPreferences {
        enabled
        smsEnabled
        phoneNumber
        signalREnabled
        updatedDate
      }
      notificationGroups {
        userId
        status
      }
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: String!) {
    deleteUser(id: $id)
  }
`;

export const UPDATE_LAST_LOGIN = gql`
  mutation UpdateLastLogin($userId: String!) {
    updateLastLogin(userId: $userId)
  }
`;

export const UPDATE_NOTIFICATION_PREFERENCES = gql`
  mutation UpdateNotificationPreferences($userId: String!, $preferences: NotificationPreferencesInput!) {
    updateNotificationPreferences(userId: $userId, preferences: $preferences)
  }
`;

export const SEND_PHONE_VERIFICATION = gql`
  mutation SendPhoneVerification($userId: String!, $phoneNumber: String!) {
    sendPhoneVerification(userId: $userId, phoneNumber: $phoneNumber)
  }
`;

export const VERIFY_PHONE_NUMBER = gql`
  mutation VerifyPhoneNumber($userId: String!, $phoneNumber: String!, $code: String!) {
    verifyPhoneNumber(userId: $userId, phoneNumber: $phoneNumber, code: $code)
  }
`;

// HierarchyLevel Mutations
export const CREATE_HIERARCHY_LEVEL = gql`
  mutation CreateHierarchyLevel($input: HierarchyLevelInput!) {
    createHierarchyLevel(input: $input) {
      id
      hierarchyLevelId
      name
      description
      createdDate
      isActive
      isSystemLevel
      isUserLevel
    }
  }
`;

export const UPDATE_HIERARCHY_LEVEL = gql`
  mutation UpdateHierarchyLevel($id: String!, $input: HierarchyLevelUpdateInput!) {
    updateHierarchyLevel(id: $id, input: $input) {
      id
      hierarchyLevelId
      name
      description
      createdDate
      isActive
      isSystemLevel
      isUserLevel
    }
  }
`;

export const DELETE_HIERARCHY_LEVEL = gql`
  mutation DeleteHierarchyLevel($id: String!) {
    deleteHierarchyLevel(id: $id)
  }
`;

export const CAN_DELETE_HIERARCHY_LEVEL = gql`
  mutation CanDeleteHierarchyLevel($id: String!) {
    canDeleteHierarchyLevel(id: $id)
  }
`;

// User Customization Mutations
export const CREATE_USER_CUSTOMIZATION = gql`
  mutation CreateUserCustomization($input: UserCustomizationInput!) {
    createUserCustomization(input: $input) {
      id
      userId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;

export const UPDATE_USER_CUSTOMIZATION = gql`
  mutation UpdateUserCustomization($id: String!, $input: UserCustomizationInput!) {
    updateUserCustomization(id: $id, input: $input) {
      id
      userId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;

export const DELETE_USER_CUSTOMIZATION = gql`
  mutation DeleteUserCustomization($id: String!) {
    deleteUserCustomization(id: $id)
  }
`;

export const UPDATE_USER_CUSTOMIZATION_FOR_USER = gql`
  mutation UpdateUserCustomizationForUser($targetUserId: String!, $id: String!, $input: UserCustomizationInput!) {
    updateUserCustomizationForUser(targetUserId: $targetUserId, id: $id, input: $input) {
      id
      userId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;

export const DELETE_USER_CUSTOMIZATION_FOR_USER = gql`
  mutation DeleteUserCustomizationForUser($targetUserId: String!, $id: String!) {
    deleteUserCustomizationForUser(targetUserId: $targetUserId, id: $id)
  }
`;

export const CREATE_USER_CUSTOMIZATION_FOR_USER = gql`
  mutation CreateUserCustomizationForUser($targetUserId: String!, $input: UserCustomizationInput!) {
    createUserCustomizationForUser(targetUserId: $targetUserId, input: $input) {
      id
      userId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;

// Tenant Customization Mutations (admin-only, roleId ≤ 2)
export const CREATE_TENANT_CUSTOMIZATION = gql`
  mutation CreateTenantCustomization($input: TenantCustomizationInput!) {
    createTenantCustomization(input: $input) {
      id
      tenantId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;

export const UPDATE_TENANT_CUSTOMIZATION = gql`
  mutation UpdateTenantCustomization($id: String!, $input: TenantCustomizationInput!) {
    updateTenantCustomization(id: $id, input: $input) {
      id
      tenantId
      label
      customizationType
      customData
      source
      sourceId
      isActive
      version
      createdDate
      updatedDate
    }
  }
`;

export const DELETE_TENANT_CUSTOMIZATION = gql`
  mutation DeleteTenantCustomization($id: String!, $tenantId: String!) {
    deleteTenantCustomization(id: $id, tenantId: $tenantId)
  }
`;
